import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { Store, Tag, User } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CREATE_STORE } from 'graphql/stores/mutations/createStore.gql';
import { GET_STORES } from 'graphql/stores/queries/getStores.gql';
import { UPDATE_STORE } from 'graphql/stores/mutations/updateStore.gql';
import { ARCHIVE_STORE } from 'graphql/stores/mutations/archiveStore.gql';
import { validateEntityName } from 'const/regex';
import { useAuthContext } from 'context/auth.context';
import { useSnackbarContext } from 'context/snackbar.context';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { hasPermissions } from 'config/ability';
import { EXPORT_STORES } from 'graphql/stores/mutations/exportStores.gql';

export interface CreateStoreInterface {
  storeNumber: string;
  name: string;
  // address: string;
  state: string;
  timezone: string;
  latlng: string;
  brandIds: number[];
  userIds: number[];
  tagIds: number[];
  storeManagerUserIds?: number[];
}

export interface UpdateStoreInterface extends CreateStoreInterface {
  id: number;
}

export interface StoreInterface extends Store {
  users: User[];
  storeManagerUsers: User[];
  tags: Tag[];
}

export interface StoresFilter {
  tenantId?: number;
  name?: string;
  state?: string;
  brandIds?: number[];
  userIds?: number[];
  storeManagerUserIds?: number[];
  tagIds?: number[];
}

interface StoresContextInterface {
  stores: StoreInterface[];
  setStores: (stores: StoreInterface[]) => void;
  storesFilter: StoresFilter;
  setStoresFilter: (filter: StoresFilter) => void;
  addStore: (store: CreateStoreInterface) => Promise<void>;
  editStore: (store: UpdateStoreInterface) => Promise<void>;
  archiveStore: (id: number) => Promise<void>;
  addStoreLoading: boolean;
  editStoreLoading: boolean;
  archiveStoreLoading: boolean;
  getStoreById: (id: number) => StoreInterface | undefined;
  exportCsv: () => Promise<void>;
  getStoreDropdownSelectOptionsByIds: (storeIds: number[]) => DropdownOption[] | undefined;
  refetchStores: () => Promise<void>;
  loading: boolean;
  error?: ApolloError;
}

const defaultContext: StoresContextInterface = {
  stores: [],
  setStores: () => {},
  storesFilter: {},
  setStoresFilter: () => {},
  addStore: async () => {},
  editStore: async () => {},
  archiveStore: async () => {},
  addStoreLoading: false,
  editStoreLoading: false,
  archiveStoreLoading: false,
  getStoreById: () => undefined,
  exportCsv: async () => {},
  getStoreDropdownSelectOptionsByIds: () => undefined,
  loading: true,
  refetchStores: async () => {},
};

const StoresContext = createContext<StoresContextInterface>(defaultContext);

export const StoresContextProvider = ({ children }: { children: ReactNode }) => {
  const [stores, setStores] = useState<StoreInterface[]>([]);
  const [createStore, { loading: addStoreLoading }] = useMutation(CREATE_STORE);
  const [updateStore, { loading: editStoreLoading }] = useMutation(UPDATE_STORE);
  const [archiveStoreQuery, { loading: archiveStoreLoading }] = useMutation(ARCHIVE_STORE);
  const [exportStoresCsv] = useMutation(EXPORT_STORES);
  const { ability, user, userTenantId } = useAuthContext();
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const [loading, setLoading] = useState<boolean>(hasPermissions(ability, 'read', 'Store'));
  const [storesFilter, setStoresFilter] = useState<StoresFilter>({
    tenantId: userTenantId,
    // Set userId filter if current user does not have permissions to view all Stores in their tenant
    userIds:
      !user || hasPermissions(ability, 'read', 'Store', { tenantId: userTenantId })
        ? []
        : [user.user.id],
  });

  useMemo(() => {
    setStoresFilter({
      ...storesFilter,
      tenantId: userTenantId,
      userIds:
        !user || hasPermissions(ability, 'read', 'Store', { tenantId: userTenantId })
          ? []
          : [user.user.id],
    });
  }, [userTenantId, user, ability]);

  // fetch stores
  const { error, refetch } = useQuery(GET_STORES, {
    skip: !userTenantId || !storesFilter.tenantId || !hasPermissions(ability, 'read', 'Store'),
    variables: {
      storesFilter,
    },
    onCompleted: ({ stores }: { stores: StoreInterface[] }) => {
      setStores(stores);
      setLoading(false);
    },
  });

  // this is an EXTRA check. validation is being handled upon name entry. in case that gets accidentally changed, this will prevent submission
  const handleValidateName = (name?: string | null) => {
    return !(!name || name.length < 2 || !validateEntityName(name));
  };

  const addStore = async (store: CreateStoreInterface) => {
    if (handleValidateName(store.name)) {
      await createStore({
        variables: {
          input: {
            ...store,
            tenantId: userTenantId,
          },
        },
      }).then(
        (result) => {
          displayApiResult(result.data.createStore, 'Created successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      await refetch();
    }
  };

  const editStore = async (input: UpdateStoreInterface) => {
    if (handleValidateName(input.name)) {
      await updateStore({
        variables: {
          input,
        },
      }).then(
        (result) => {
          displayApiResult(result.data.updateStore, 'Updated successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      await refetch();
    }
  };

  const archiveStore = async (id: number) => {
    await archiveStoreQuery({
      variables: {
        id: id,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.archiveStore, 'Archived successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  const getStoreById = (id: number) => {
    return stores.find((store) => store.id === id);
  };

  const exportCsv = async () => {
    await exportStoresCsv({
      variables: {
        input: storesFilter,
      },
    }).then(
      (result) => {
        const csvDataEncoded = result.data.exportStores.file.encoding;
        const dataUrl = `data:${result.data.exportStores.file.mimetype};base64,${csvDataEncoded}`;

        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = result.data.exportStores.file.filename;
        link.click();

        displayApiResult(result.data.exportStores, 'CSV Created Successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  // get store dropdown select options from an array of store ids
  const getStoreDropdownSelectOptionsByIds = (storeIds: number[]) => {
    const stores = storeIds.map((id) => getStoreById(id));
    const storesDropdownOptions: DropdownOption[] = [];
    stores.map((store) => store && storesDropdownOptions.push({ name: store.name, id: store.id }));
    return storesDropdownOptions;
  };

  const refetchStores = async () => {
    await refetch();
  };

  return (
    <StoresContext.Provider
      value={{
        stores,
        setStores,
        storesFilter,
        setStoresFilter,
        addStore,
        editStore,
        archiveStore,
        addStoreLoading,
        editStoreLoading,
        archiveStoreLoading,
        getStoreById,
        exportCsv,
        getStoreDropdownSelectOptionsByIds,
        refetchStores,
        loading,
        error,
      }}
    >
      {children}
    </StoresContext.Provider>
  );
};

export const useStoresContext = () => useContext(StoresContext);
